import { Pouch, Group } from '../types';
import { POUCH_VALUE_STEP, roundPouchValue } from '../utils/pouchValueRounding';
import { applyPouchScaleAndRound, roundScaledValue } from '../utils/pouchScaleAndRound';
import {
  TIME_POUCH_BASE_VALUE,
  timePouchStateFromEffectiveMinutes,
} from '../utils/timePouchState';
import {
  calculateGroupSubtotal,
  groupHasDivisionInclusiveGtp,
  isInclusiveGtpOfGroup,
  findGroupInclusiveGtps,
  getInclusiveGtpMergedGroupSheetTotal,
  getInclusiveGtpSheetPrimaryPouchId,
  getInheritedRoundUnitFromInclusiveMulGtp,
  isInMuteGroup,
  computeGroupInclusiveGtpMergeFactor,
  resolveInclusiveMulLeafAggregateContribution,
} from './GroupLogic';

/**
 * パウチのシート合計寄与（実効値・IDGTP のマージ計上）および帳尻パウチ（BP）。
 * IDGTP の葉への按分表示は InclusiveDivGtpLogic（ファイル名に「帳尻」がないのは意図どおり）。
 */

/** 時間パウチの内部値の絶対値（UI の「±60×」と対応。負の時間は value=-60・マルチは正の分率） */
export const TIME_POUCH_BALANCING_BASE_VALUE = TIME_POUCH_BASE_VALUE;

/** 時間パウチの合計への寄与（value×multiplier、roundUnit があれば実効分数を丸め） */
export const getTimePouchEffectiveForTotal = (p: Pouch): number => applyPouchScaleAndRound(p);

/** 帳尻用: 必要な実効値（分）から time BP の state(value/multiplier)を作る */
export const getTimeBalancingStateFromEffective = timePouchStateFromEffectiveMinutes;

/**
 * 表示・サイズ計算用: 時間の実効分数（レガシー除算モードは分岐）
 * multiplierOverride: 一時マルチ（ボタン操作中）
 */
export const getTimePouchEffectiveMinutes = (
  p: Pick<Pouch, 'value' | 'multiplier' | 'isTime'>,
  multiplierOverride?: number
): number => {
  const m = multiplierOverride !== undefined ? multiplierOverride : p.multiplier;
  if (!p.isTime) return p.value;
  return m >= 0 ? p.value * m : p.value / Math.abs(m);
};

export {
  applyTimeEffectiveGtpBake,
  getTimeLayerEffectiveMinutes,
} from '../utils/timePouchState';

/** シート・グループ小計と同じ定義のパウチ1個分の実効値 */
export const getPouchEffectiveContribution = (
  p: Pouch,
  groups?: Group[],
  allPouches?: Pouch[],
  contextGroupId?: string,
  balancingPouchId?: string | null,
): number => {
  if (groups && allPouches) {
    const aggregate = resolveInclusiveMulLeafAggregateContribution(
      p,
      groups,
      allPouches,
      contextGroupId,
      balancingPouchId,
    );
    if (aggregate !== undefined) {
      return aggregate;
    }
    const parents = contextGroupId
      ? groups.filter(g => g.id === contextGroupId)
      : groups.filter(g => g.pouches?.includes(p.id));
    for (const g of parents) {
      const inherited = getInheritedRoundUnitFromInclusiveMulGtp(p, g, allPouches, groups);
      if (inherited !== undefined) {
        return applyPouchScaleAndRound({ ...p, roundUnit: inherited });
      }
    }
  }
  return applyPouchScaleAndRound(p);
};

/** 親グループのいずれかに ÷内包 GTP があり、自分がその内包 GTP でないときは計上しない（シート／グループ枠 TOTAL と一致） */
export const getPouchContributionForTotals = (
  pouch: Pouch,
  groups: Group[],
  allPouches: Pouch[],
  contextGroupId?: string,
  balancingPouchId?: string | null,
): number => {
  const parents = groups.filter(g => g.pouches?.includes(pouch.id));
  const scopeParents = contextGroupId
    ? parents.filter(g => g.id === contextGroupId)
    : parents;
  for (const g of scopeParents) {
    if (groupHasDivisionInclusiveGtp(g, allPouches)) {
      if (!isInclusiveGtpOfGroup(pouch.id, g, allPouches)) {
        return 0;
      }
    }
  }
  // ÷内包: マージ係数での合成とシート計上を一致（×のみ加算では ×混在÷ と噛み合わない）
  for (const g of scopeParents) {
    if (!groupHasDivisionInclusiveGtp(g, allPouches)) continue;
    if (!isInclusiveGtpOfGroup(pouch.id, g, allPouches)) continue;
    const primaryId = getInclusiveGtpSheetPrimaryPouchId(g, allPouches);
    if (primaryId !== pouch.id) {
      return 0;
    }
    const merged = getInclusiveGtpMergedGroupSheetTotal(g, allPouches, groups);
    if (merged !== null) {
      return merged;
    }
    break;
  }
  return getPouchEffectiveContribution(
    pouch,
    groups,
    allPouches,
    contextGroupId,
    balancingPouchId,
  );
};

/**
 * 帳尻用: 必要な実効値から value=±60 と multiplier（分率・非負）を求める。
 * IDGTP マージ葉の時間 BP は `otherTotal` だけでは合わないことがあり、その場合は感度調整が別途必要。
 */
export const calculateBalancingPouchTimeState = (
  pouches: Pouch[],
  groups: Group[],
  balancingPouchId: string,
  targetTotal: number
): { value: number; multiplier: number } => {
  const bpPouch = pouches.find(p => p.id === balancingPouchId);
  if (!bpPouch) return { value: 0, multiplier: 0 };

  const otherTotal = calculateNonBalancingTotal(pouches, groups, balancingPouchId);
  const requiredEffective = targetTotal - otherTotal;
  return getTimeBalancingStateFromEffective(requiredEffective);
};

/**
 * BP 以外の全パウチの effectiveValue 合計を計算する
 */
export const calculateNonBalancingTotal = (
  pouches: Pouch[],
  groups: Group[],
  balancingPouchId: string
): number => {
  return pouches
    .filter(p =>
      p.id !== balancingPouchId &&
      !p._isDeleting &&
      !p._isGtpRenderPreview &&
      !isInMuteGroup(p.id, groups)
    )
    .reduce(
      (sum, p) =>
        sum + getPouchContributionForTotals(p, groups, pouches, undefined, balancingPouchId),
      0,
    );
};

/**
 * 全パウチの合計を計算する（ターゲット記録用）
 */
export const calculateSheetTotal = (
  pouches: Pouch[],
  groups: Group[],
  balancingPouchId?: string | null,
): number => {
  const total = pouches
    .filter(p => !p._isDeleting && !p._isGtpRenderPreview && !isInMuteGroup(p.id, groups))
    .reduce(
      (sum, p) =>
        sum + getPouchContributionForTotals(p, groups, pouches, undefined, balancingPouchId),
      0,
    );
  return Math.round(total * 1000000000) / 1000000000;
};

const pickBetterBalancingCandidate = (
  a: { value: number; error: number },
  b: { value: number; error: number },
  options?: { anchorValue?: number; preferredSign?: 1 | -1; targetHint?: number },
): { value: number; error: number } => {
  const anchorValue = options?.anchorValue;
  const preferredSign = options?.preferredSign;
  const targetHint = options?.targetHint;

  const errAbs = (c: { value: number; error: number }) => Math.abs(c.error);
  const signScore = (v: number) => {
    if (preferredSign) return v * preferredSign >= 0 ? 0 : 1;
    return v >= 0 ? 0 : 1;
  };
  const integerScore = (v: number) =>
    Math.abs(v - Math.round(v)) < 1e-9 ? 0 : 1;
  const hintDistance = (v: number) =>
    targetHint !== undefined ? Math.abs(v - targetHint) : undefined;

  const aErr = errAbs(a);
  const bErr = errAbs(b);
  if (bErr < aErr - 1e-12) return b;
  if (aErr < bErr - 1e-12) return a;

  const aSign = signScore(a.value);
  const bSign = signScore(b.value);
  if (bSign < aSign) return b;
  if (aSign < bSign) return a;

  const aInt = integerScore(a.value);
  const bInt = integerScore(b.value);
  if (bInt < aInt) return b;
  if (aInt < bInt) return a;

  if (anchorValue !== undefined) {
    const da = Math.abs(a.value - anchorValue);
    const db = Math.abs(b.value - anchorValue);
    if (db + 1e-12 < da) return b;
    if (da + 1e-12 < db) return a;
  }

  if (targetHint !== undefined) {
    const da = hintDistance(a.value)!;
    const db = hintDistance(b.value)!;
    if (db + 1e-12 < da) return b;
    if (da + 1e-12 < db) return a;
  }

  if (b.value >= 0 && a.value < 0) return b;
  if (a.value >= 0 && b.value < 0) return a;
  return Math.abs(b.value) < Math.abs(a.value) ? b : a;
};

/** ×/÷ 参照 GTP 連立: BP 実効が葉 S に効く倍率（×は +m、グループ外 ÷ は +1/|m|） */
const getBalancingGtpCouplingFactor = (
  pouches: Pouch[],
  groups: Group[],
  balancingPouchId: string,
): number => {
  let factor = 1;
  pouches.forEach((p) => {
    if (!p.isGroupTotalPouch || !p.referencedGroupId || p._isDeleting) return;
    const refGroup = groups.find((g) => g.id === p.referencedGroupId);
    if (!refGroup?.pouches?.includes(balancingPouchId)) return;
    if (groupHasDivisionInclusiveGtp(refGroup, pouches)) return;

    if (p.multiplier >= 0) {
      factor += p.multiplier;
      return;
    }
    // グループ外 ÷GTP: 寄与 ≈ S/|m|（value ミラー）。内包 ÷ は addDivisionInclusive 側
    if (!isInclusiveGtpOfGroup(p.id, refGroup, pouches)) {
      const d = Math.abs(p.multiplier);
      if (d > 0) factor += 1 / d;
    }
  });
  return factor;
};

/** BP が所属する ÷内包グループ（葉小計 S に merge factor を掛けてシート計上） */
const getBalancingDivisionInclusiveGroups = (
  pouches: Pouch[],
  groups: Group[],
  balancingPouchId: string,
): Group[] =>
  groups.filter(
    (g) =>
      g.pouches?.includes(balancingPouchId) &&
      groupHasDivisionInclusiveGtp(g, pouches),
  );

const effectiveToBalancingPouchValue = (
  effective: number,
  mult: number,
): number => {
  if (mult === 0) return 0;
  return roundPouchValue(
    mult < 0 ? effective * Math.abs(mult) : effective / mult,
  );
};

const addBalancingEffectiveCandidates = (
  candidates: Set<number>,
  targetEffective: number,
  mult: number,
  roundUnit?: number,
) => {
  if (mult === 0) return;
  if (roundUnit !== undefined) {
    const baseEff = roundScaledValue(targetEffective, roundUnit);
    for (const step of [-2, -1, 0, 1, 2]) {
      candidates.add(
        effectiveToBalancingPouchValue(baseEff + step * roundUnit, mult),
      );
    }
    return;
  }
  candidates.add(effectiveToBalancingPouchValue(targetEffective, mult));
};

/** BP 評価用: BP value を差し替え → GTP ミラー（×・÷内包） → シート合計 */
const totalWithBalancingPouchValue = (
  pouches: Pouch[],
  groups: Group[],
  balancingPouchId: string,
  value: number,
): number => {
  const v = roundPouchValue(value);
  const mutated = pouches.map((p) =>
    p.id === balancingPouchId ? { ...p, value: v, isNegative: v < 0 } : p,
  );
  const stabilized = syncMirroredGtpValuesForBalancing(mutated, groups);
  return calculateSheetTotal(stabilized, groups, balancingPouchId);
};

/** ÷内包 GTP の value を葉小計 S へ同期（UI ミラーと同じ。S=0 時の stale 値による偽解を防ぐ） */
const syncMirroredDivInclusiveGtpValues = (
  pouches: Pouch[],
  groups: Group[],
): Pouch[] => {
  let next = [...pouches];
  for (let i = 0; i < 4; i++) {
    let changed = false;
    const divGtps = next.filter(
      (p) =>
        p.isGroupTotalPouch &&
        !!p.referencedGroupId &&
        p.multiplier < 0 &&
        !p._isDeleting,
    );
    divGtps.forEach((gtp) => {
      const referencedGroup = groups.find((g) => g.id === gtp.referencedGroupId);
      if (!referencedGroup) return;
      if (!isInclusiveGtpOfGroup(gtp.id, referencedGroup, next)) return;
      const groupPouches = next.filter(
        (p) =>
          referencedGroup.pouches?.includes(p.id) &&
          !p._isDeleting &&
          !(p.isGroupTotalPouch && p.referencedGroupId === referencedGroup.id) &&
          !isInMuteGroup(p.id, groups),
      );
      const pureSubtotal = calculateGroupSubtotal(
        referencedGroup.id,
        groupPouches,
        groups,
        next,
      );
      const mirroredValue = pureSubtotal * (gtp.oppositeToTotal ? -1 : 1);
      const idx = next.findIndex((p) => p.id === gtp.id);
      if (
        idx >= 0 &&
        (next[idx].value !== mirroredValue ||
          next[idx].isNegative !== (mirroredValue < 0))
      ) {
        next[idx] = {
          ...next[idx],
          value: mirroredValue,
          isNegative: mirroredValue < 0,
        };
        changed = true;
      }
    });
    if (!changed) break;
  }
  return next;
};

/** グループ外 ÷GTP の value を葉小計へ同期（×GTP と同型ミラー。寄与は value/|m|） */
const syncMirroredExternalDivGtpValues = (
  pouches: Pouch[],
  groups: Group[],
): Pouch[] => {
  let next = [...pouches];
  for (let i = 0; i < 4; i++) {
    let changed = false;
    const divGtps = next.filter(
      (p) =>
        p.isGroupTotalPouch &&
        !!p.referencedGroupId &&
        p.multiplier < 0 &&
        !p._isDeleting,
    );
    divGtps.forEach((gtp) => {
      const referencedGroup = groups.find((g) => g.id === gtp.referencedGroupId);
      if (!referencedGroup) return;
      if (isInclusiveGtpOfGroup(gtp.id, referencedGroup, next)) return;
      const groupPouches = next.filter(
        (p) =>
          referencedGroup.pouches?.includes(p.id) &&
          !p._isDeleting &&
          !(p.isGroupTotalPouch && p.referencedGroupId === referencedGroup.id) &&
          !isInMuteGroup(p.id, groups),
      );
      const pureSubtotal = calculateGroupSubtotal(
        referencedGroup.id,
        groupPouches,
        groups,
        next,
      );
      const mirroredValue = pureSubtotal * (gtp.oppositeToTotal ? -1 : 1);
      const idx = next.findIndex((p) => p.id === gtp.id);
      if (
        idx >= 0 &&
        (next[idx].value !== mirroredValue ||
          next[idx].isNegative !== (mirroredValue < 0))
      ) {
        next[idx] = {
          ...next[idx],
          value: mirroredValue,
          isNegative: mirroredValue < 0,
        };
        changed = true;
      }
    });
    if (!changed) break;
  }
  return next;
};

/** BP 再計算・候補評価用: ×GTP / 内包÷ / グループ外÷ の value を葉小計へ同期 */
const syncMirroredGtpValuesForBalancing = (
  pouches: Pouch[],
  groups: Group[],
): Pouch[] =>
  syncMirroredExternalDivGtpValues(
    syncMirroredDivInclusiveGtpValues(syncMirroredMulGtpValues(pouches, groups), groups),
    groups,
  );

/** ÷内包グループ内 BP: merge factor から必要葉小計 S を逆算して候補を追加 */
const addDivisionInclusiveBalancingCandidates = (
  candidates: Set<number>,
  pouches: Pouch[],
  groups: Group[],
  balancingPouchId: string,
  targetTotal: number,
  bpMult: number,
  bpRoundUnit?: number,
) => {
  if (bpMult === 0) return;
  const divGroups = getBalancingDivisionInclusiveGroups(
    pouches,
    groups,
    balancingPouchId,
  );
  if (divGroups.length === 0) return;

  for (const group of divGroups) {
    const gtps = findGroupInclusiveGtps(group, pouches);
    const gtpIds = new Set(gtps.map((g) => g.id));
    const leavesWithoutBp = pouches.filter(
      (p) =>
        group.pouches?.includes(p.id) &&
        p.id !== balancingPouchId &&
        !gtpIds.has(p.id) &&
        !p._isDeleting &&
        !isInMuteGroup(p.id, groups),
    );
    const SWithoutBp = calculateGroupSubtotal(group.id, leavesWithoutBp, groups, pouches);

    const zeroBp = pouches.map((p) =>
      p.id === balancingPouchId ? { ...p, value: 0, isNegative: false } : p,
    );
    const syncedZero = syncMirroredGtpValuesForBalancing(zeroBp, groups);
    const totalAtZero = calculateSheetTotal(syncedZero, groups, balancingPouchId);
    const mergedAtZero =
      getInclusiveGtpMergedGroupSheetTotal(group, syncedZero, groups) ?? totalAtZero;

    const factor =
      computeGroupInclusiveGtpMergeFactor(group, syncedZero, groups) ??
      (Math.abs(SWithoutBp) > 1e-12 ? mergedAtZero / SWithoutBp : null);
    if (factor === null || Math.abs(factor) < 1e-12) continue;

    const bpEffPerValue =
      bpMult >= 0 ? bpMult : 1 / Math.abs(bpMult);
    const requiredS = targetTotal / factor;
    const requiredBpEff = requiredS - SWithoutBp;
    const rawValue = requiredBpEff / bpEffPerValue;

    if (bpRoundUnit !== undefined) {
      const baseEff = roundScaledValue(requiredBpEff, bpRoundUnit);
      for (const step of [-3, -2, -1, 0, 1, 2, 3]) {
        const eff = baseEff + step * bpRoundUnit;
        candidates.add(
          effectiveToBalancingPouchValue(eff, bpMult),
        );
      }
    } else {
      candidates.add(effectiveToBalancingPouchValue(requiredBpEff, bpMult));
      for (let d = -5; d <= 5; d++) {
        candidates.add(roundPouchValue(rawValue + d * POUCH_VALUE_STEP));
      }
    }
  }
};

/**
 * 非時間 BP: 候補 value を総当たりし |合計−目標| 最小を選ぶ（GTP ~N の離散化・BP↔GTP 連立に対応）。
 */
const searchBestBalancingNumericValue = (
  pouches: Pouch[],
  groups: Group[],
  balancingPouchId: string,
  targetTotal: number,
  preferredSign?: 1 | -1,
): number => {
  const bpPouch = pouches.find((p) => p.id === balancingPouchId);
  if (!bpPouch) return 0;

  const stabilized = syncMirroredGtpValuesForBalancing(pouches, groups);
  const currentValue = roundPouchValue(bpPouch.value);
  const currentTotal = calculateSheetTotal(stabilized, groups, balancingPouchId);
  const otherTotal = calculateNonBalancingTotal(stabilized, groups, balancingPouchId);
  const mult = bpPouch.multiplier;
  const bpRoundUnit =
    bpPouch.roundUnit !== undefined && bpPouch.roundUnit > 0 ? bpPouch.roundUnit : undefined;

  let linearGuess = currentValue;
  if (mult !== 0) {
    const requiredEffective = targetTotal - otherTotal;
    if (bpRoundUnit !== undefined) {
      const targetEff = roundScaledValue(requiredEffective, bpRoundUnit);
      linearGuess = roundPouchValue(
        mult < 0 ? targetEff * Math.abs(mult) : targetEff / mult,
      );
    } else {
      linearGuess = roundPouchValue(
        mult < 0 ? requiredEffective * Math.abs(mult) : requiredEffective / mult,
      );
    }
  }

  const candidates = new Set<number>([currentValue, linearGuess]);

  addDivisionInclusiveBalancingCandidates(
    candidates,
    stabilized,
    groups,
    balancingPouchId,
    targetTotal,
    mult,
    bpRoundUnit,
  );

  let coupledHint: number | undefined;
  const couplingFactor = getBalancingGtpCouplingFactor(
    stabilized,
    groups,
    balancingPouchId,
  );
  if (couplingFactor > 1 && mult !== 0) {
    const zeroBp = stabilized.map((p) =>
      p.id === balancingPouchId ? { ...p, value: 0, isNegative: false } : p,
    );
    const totalAtZero = calculateSheetTotal(
      syncMirroredGtpValuesForBalancing(zeroBp, groups),
      groups,
      balancingPouchId,
    );
    const coupledEffective = (targetTotal - totalAtZero) / couplingFactor;
    addBalancingEffectiveCandidates(candidates, coupledEffective, mult, bpRoundUnit);
    coupledHint = effectiveToBalancingPouchValue(coupledEffective, mult);
    if (bpRoundUnit !== undefined) {
      coupledHint = effectiveToBalancingPouchValue(
        roundScaledValue(coupledEffective, bpRoundUnit),
        mult,
      );
    }
    if (bpRoundUnit !== undefined) {
      const baseEff = roundScaledValue(coupledEffective, bpRoundUnit);
      for (const step of [-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5]) {
        candidates.add(
          effectiveToBalancingPouchValue(baseEff + step * bpRoundUnit, mult),
        );
      }
    }
  }

  if (bpRoundUnit !== undefined && mult !== 0) {
    const requiredEffective = targetTotal - otherTotal;
    const baseEff = roundScaledValue(requiredEffective, bpRoundUnit);
    for (const step of [-1, 0, 1]) {
      const targetEff = baseEff + step * bpRoundUnit;
      candidates.add(
        roundPouchValue(
          mult < 0 ? targetEff * Math.abs(mult) : targetEff / mult,
        ),
      );
    }
  }
  for (let d = -15; d <= 15; d++) {
    candidates.add(roundPouchValue(linearGuess + d * POUCH_VALUE_STEP));
    candidates.add(roundPouchValue(currentValue + d * POUCH_VALUE_STEP));
  }

  let sensitivityHint: number | undefined;
  const plusTotal = totalWithBalancingPouchValue(
    stabilized,
    groups,
    balancingPouchId,
    currentValue + POUCH_VALUE_STEP,
  );
  const sens = plusTotal - currentTotal;
  if (Math.abs(sens) > 1e-9) {
    sensitivityHint = roundPouchValue(
      currentValue + ((targetTotal - currentTotal) / sens) * POUCH_VALUE_STEP,
    );
    candidates.add(sensitivityHint);
    const span = Math.min(
      400,
      Math.max(15, Math.ceil(Math.abs(sensitivityHint - currentValue) / POUCH_VALUE_STEP) + 5),
    );
    for (let d = -span; d <= span; d++) {
      candidates.add(roundPouchValue(sensitivityHint + d * POUCH_VALUE_STEP));
    }
  }

  const targetHint = sensitivityHint ?? coupledHint ?? linearGuess;
  let best = { value: currentValue, error: currentTotal - targetTotal };
  const pickOpts = { anchorValue: currentValue, preferredSign, targetHint };
  for (const raw of Array.from(candidates)) {
    if (!Number.isFinite(raw)) continue;
    const v = roundPouchValue(raw);
    const err =
      totalWithBalancingPouchValue(stabilized, groups, balancingPouchId, v) - targetTotal;
    best = pickBetterBalancingCandidate(best, { value: v, error: err }, pickOpts);
  }

  return best.value;
};

/**
 * 乗算系GTP（multiplier>=0）の value を、参照グループ小計のミラー値へ同期する。
 * BP候補評価/適用と同時に更新しないと、フレーム跨ぎで古い GTP 値を参照して振動しやすい。
 */
const syncMirroredMulGtpValues = (
  pouches: Pouch[],
  groups: Group[]
): Pouch[] => {
  let next = [...pouches];
  for (let i = 0; i < 4; i++) {
    let changed = false;
    const mulGtps = next.filter(
      (p) => p.isGroupTotalPouch && !!p.referencedGroupId && p.multiplier >= 0 && !p._isDeleting
    );
    mulGtps.forEach((gtp) => {
      const referencedGroup = groups.find((g) => g.id === gtp.referencedGroupId);
      if (!referencedGroup) return;
      if (
        groupHasDivisionInclusiveGtp(referencedGroup, next) &&
        isInclusiveGtpOfGroup(gtp.id, referencedGroup, next)
      ) {
        return;
      }
      const groupPouches = next.filter((p) =>
        referencedGroup.pouches?.includes(p.id) &&
        !p._isDeleting &&
        !(p.isGroupTotalPouch && p.referencedGroupId === referencedGroup.id) &&
        !isInMuteGroup(p.id, groups)
      );
      const pureSubtotal = calculateGroupSubtotal(referencedGroup.id, groupPouches, groups, next);
      const mirroredValue = pureSubtotal * (gtp.oppositeToTotal ? -1 : 1);
      const idx = next.findIndex((p) => p.id === gtp.id);
      if (idx >= 0 && (next[idx].value !== mirroredValue || next[idx].isNegative !== (mirroredValue < 0))) {
        next[idx] = { ...next[idx], value: mirroredValue, isNegative: mirroredValue < 0 };
        changed = true;
      }
    });
    if (!changed) break;
  }
  return next;
};

/**
 * BP パウチの新しい value を計算する
 * ターゲット合計に最も近くなる value（小数第2位）を返す
 */
export const calculateBalancingPouchValue = (
  pouches: Pouch[],
  groups: Group[],
  balancingPouchId: string,
  targetTotal: number,
  preferredSign?: 1 | -1,
): number => {
  const bpPouch = pouches.find((p) => p.id === balancingPouchId);
  if (!bpPouch || bpPouch.isTime === true) return 0;
  return searchBestBalancingNumericValue(
    pouches,
    groups,
    balancingPouchId,
    targetTotal,
    preferredSign,
  );
};

/**
 * BP 再計算を適用した pouches 配列を返す
 * 通常 BP は value のみ更新。時間パウチ BP は value=±60 と multiplier を更新する。
 */
export const applyBalancingPouch = (
  pouches: Pouch[],
  groups: Group[],
  balancingPouchId: string,
  targetTotal: number,
  preferredSign?: 1 | -1
): Pouch[] => {
  const bpPouch = pouches.find(p => p.id === balancingPouchId);
  if (!bpPouch) return pouches;
  const sheetHasInclusiveGtp = groups.some((g) => findGroupInclusiveGtps(g, pouches).length > 0);
  const bpCouplesWithExternalGtp = pouches.some((p) => {
    if (!p.isGroupTotalPouch || !p.referencedGroupId || p._isDeleting) return false;
    const refGroup = groups.find((g) => g.id === p.referencedGroupId);
    return !!refGroup?.pouches?.includes(balancingPouchId);
  });
  const requiresFixedPointSolve = sheetHasInclusiveGtp || bpCouplesWithExternalGtp;

  if (bpPouch.isTime === true) {
    // 時刻 BP でも GTP と相互依存する場合は、実測感度で係数を推定して解く。
    // 例: TOTAL = k * BP のとき BP = target / k を近傍探索で安定に求める。
    if (requiresFixedPointSolve) {
      const totalWithBpEffective = (effectiveMinutes: number): number => {
        const nextState = getTimeBalancingStateFromEffective(effectiveMinutes);
        const mutated = pouches.map((p) =>
          p.id === balancingPouchId
            ? { ...p, value: nextState.value, multiplier: nextState.multiplier }
            : p
        );
        const stabilized = syncMirroredGtpValuesForBalancing(mutated, groups);
        return calculateSheetTotal(stabilized, groups, balancingPouchId);
      };
      const currentEffective = getTimePouchEffectiveForTotal(bpPouch);
      const pickOpts = { anchorValue: currentEffective, preferredSign };
      const pickBetterCandidate = (
        a: { effective: number; error: number },
        b: { effective: number; error: number },
      ): { effective: number; error: number } => {
        const picked = pickBetterBalancingCandidate(
          { value: a.effective, error: a.error },
          { value: b.effective, error: b.error },
          pickOpts,
        );
        return { effective: picked.value, error: picked.error };
      };

      const stabilizedCurrent = syncMirroredGtpValuesForBalancing(pouches, groups);
      const currentTotal = calculateSheetTotal(stabilizedCurrent, groups, balancingPouchId);
      const plusTotal = totalWithBpEffective(currentEffective + 1);
      const sens = plusTotal - currentTotal;

      if (Math.abs(sens) > 1e-9) {
        const predictedEffective =
          currentEffective + Math.round((targetTotal - currentTotal) / sens);
        const candidates = new Set<number>([
          currentEffective,
          currentEffective - 1,
          currentEffective + 1,
          predictedEffective,
          predictedEffective - 3,
          predictedEffective - 2,
          predictedEffective - 1,
          predictedEffective + 1,
          predictedEffective + 2,
          predictedEffective + 3,
        ]);

        let best = { effective: currentEffective, error: currentTotal - targetTotal };
        Array.from(candidates).forEach((e) => {
          const err = totalWithBpEffective(e) - targetTotal;
          best = pickBetterCandidate(best, { effective: e, error: err });
        });
        const pickedEffective = best.effective;
        const nextState = getTimeBalancingStateFromEffective(pickedEffective);
        const mutated = pouches.map((p) =>
          p.id === balancingPouchId
            ? { ...p, value: nextState.value, multiplier: nextState.multiplier }
            : p
        );
        return syncMirroredGtpValuesForBalancing(mutated, groups);
      }

      // 感度がほぼ 0 のときは、従来どおり固定点反復で暴れを抑える。
      let working = [...pouches];
      for (let i = 0; i < 6; i++) {
        const { value: newValue, multiplier: newMultiplier } = calculateBalancingPouchTimeState(
          working,
          groups,
          balancingPouchId,
          targetTotal
        );
        const withBp = working.map((p) =>
          p.id === balancingPouchId ? { ...p, value: newValue, multiplier: newMultiplier } : p
        );
        const stabilized = syncMirroredGtpValuesForBalancing(withBp, groups);
        const same = stabilized.every(
          (p, idx) =>
            p.id === working[idx].id &&
            p.value === working[idx].value &&
            p.multiplier === working[idx].multiplier
        );
        working = stabilized;
        if (same) break;
      }
      return working;
    }
    const { value: newValue, multiplier: newMultiplier } = calculateBalancingPouchTimeState(
      pouches,
      groups,
      balancingPouchId,
      targetTotal
    );
    return pouches.map(p =>
      p.id === balancingPouchId
        ? { ...p, value: newValue, multiplier: newMultiplier }
        : p
    );
  }

  const stabilized = syncMirroredGtpValuesForBalancing(pouches, groups);
  const newValue = calculateBalancingPouchValue(
    pouches,
    groups,
    balancingPouchId,
    targetTotal,
    preferredSign,
  );
  const withBp = stabilized.map((p) =>
    p.id === balancingPouchId
      ? { ...p, value: newValue, isNegative: newValue < 0 }
      : p,
  );
  return syncMirroredGtpValuesForBalancing(withBp, groups);
};
